import React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import _ from 'lodash';
import { Actions } from '../../store/actions/facilityActions';
import { checkReportingYearEndAndIndustry } from '../utils/GlobalFunctions';
import FacilityRow from './FacilityRow';
import DeleteFacilityModal from './DeleteFacilityModal';
import UploadFacilityData from './UploadFacilityData';
import { NoPermissions } from '../Common/ReusableComponents/Reusable';

import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from '../ui/table';
import { Spinner } from '../ui/spinner';
import { CloudUpload } from 'lucide-react';

const FacilityTable = ({
  list,
  currentOrganisation,
  showFacilityImportModal,
  branding,
  showLoader,
  addFacilityIssue,
  canManageOrg,
  canView,
}) => {
  const { body } = branding.fonts?.[0].font || {};
  const history = useHistory();

  const sortedFacilityList = _.sortBy(_.uniqBy(list, 'id'), ['name']);

  const handleAddFacility = () => {
    // if issue to add facility then redirect to organisation page, otherwise redirect to add facility page
    addFacilityIssue ? history.push(`/organisations/${currentOrganisation}`) : history.push('/facilities/new');
  };

  return (
    <>
      {!canView && <NoPermissions />}
      {canView && (
        <div className='w-full' style={{ fontFamily: body }}>
          <Card className='border rounded-md'>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='uppercase text-xl font-semibold'>Your Facilities</CardTitle>
              {!showLoader && canManageOrg && (
                <div className='flex flex-row gap-4'>
                  <Button
                    id='add-facility-button'
                    onClick={handleAddFacility}
                    className='font-semibold text-base bg-secondary text-secondary-foreground hover:text-primary-foreground'>
                    {addFacilityIssue ? `${addFacilityIssue} to add a facility` : 'Add a Facility'}
                  </Button>
                  <Button
                    className='font-semibold text-base bg-secondary text-secondary-foreground hover:text-primary-foreground'
                    onClick={() => showFacilityImportModal(true)}>
                    <CloudUpload size={16} strokeWidth={1.5} />
                    Import Facilities
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className='p-0'>
              {showLoader && !list.length ? (
                <div className='mt-4 w-full flex items-center justify-center p-8'>
                  <Spinner size='large' />
                </div>
              ) : (
                <div className='overflow-x-auto m-4'>
                  <Table className='w-full'>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Facility</TableHead>
                        <TableHead>Primary</TableHead>
                        <TableHead>Post code</TableHead>
                        <TableHead>External ID</TableHead>
                        <TableHead className='text-center'>Metrics</TableHead>
                        <TableHead className='text-center'>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {list &&
                        Array.isArray(sortedFacilityList) &&
                        sortedFacilityList.map((facilityDetails, index) => (
                          <FacilityRow key={index} facilityDetails={facilityDetails} />
                        ))}
                      {sortedFacilityList?.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className='text-center font-semibold text-base'>
                            No facilities available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <UploadFacilityData />
          <DeleteFacilityModal />
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  const orgDetails = state?.organisation?.details;
  const addFacilityIssue = checkReportingYearEndAndIndustry(orgDetails);

  const facilityPermissions = _.omit(state?.organisation?.permissions?.facilities || {}, '*');
  const specificFacilityPermissions = _.some(facilityPermissions, 'view');

  return {
    list: state.facility?.list || [],
    currentOrganisation: state.currentOrganisation,
    branding: state.branding,
    showLoader: state?.showLoader,
    addFacilityIssue,
    canManageOrg: !!state?.organisation?.permissions?.organisation?.manage,
    canView: state?.organisation?.permissions?.facilities?.['*']?.view || specificFacilityPermissions,
  };
};

const mapDispatchToProps = (dispatch) => ({
  showFacilityImportModal: (status) => dispatch(Actions.showFacilityImportModal(status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FacilityTable);
